import { lazy } from "react"
import { createBrowserRouter, Navigate } from "react-router"
import App from "./App.tsx"
import { DashboardLayout, LazyRoute } from "./layouts"
// PERF: RetailerSupplyView drives the /shipments planning console — a heavy,
// frequently-hit route — so keep it eager to avoid a dynamic-import round-trip
// on navigation. (The app now lands on /data, not /shipments; see index route.)
import { RetailerSupplyView } from "./containers/RetailerSupplyView"
// NucleusChatView is also used by DashboardLayout as a dock, so it ships in the
// main bundle anyway — import eagerly to avoid a useless dynamic-import hop.
import { NucleusChatView } from "./containers/NucleusChatView"
import { nucleusWorkflowsEnabled } from "./configs/nucleusWorkflows"
// Tiny constants (no component code) driving the pinned Demand route below.
import {
  DEMAND_BASE_PATH,
  DEMAND_REPORT_SLUG,
} from "./containers/ReportingView/constants"

// PERF: non-default routes stay lazy — smaller initial bundle.
const DataHubView = lazy(() =>
  import("./containers/DataHubView").then((m) => ({ default: m.DataHubView })),
)
const MappingDetailView = lazy(() =>
  import("./containers/MappingDetailView").then((m) => ({ default: m.MappingDetailView })),
)
const DataSourceDetailView = lazy(() =>
  import("./containers/DataSourceDetailView").then((m) => ({ default: m.DataSourceDetailView })),
)
const RetailValidationView = lazy(() =>
  import("./containers/RetailValidationView").then((m) => ({
    default: m.RetailValidationView,
  })),
)
const ShipmentsValidationView = lazy(() =>
  import("./containers/ShipmentsValidationView").then((m) => ({
    default: m.ShipmentsValidationView,
  })),
)
const ShipmentsConfigurationView = lazy(() =>
  import("./containers/ShipmentsConfigurationView").then((m) => ({
    default: m.ShipmentsConfigurationView,
  })),
)
const ShipmentsReportingView = lazy(() =>
  import("./containers/ShipmentsReportingView").then((m) => ({
    default: m.ShipmentsReportingView,
  })),
)
const RunHistoryView = lazy(() =>
  import("./containers/RunHistoryView").then((m) => ({ default: m.RunHistoryView })),
)
const ReportingView = lazy(() =>
  import("./containers/ReportingView").then((m) => ({ default: m.ReportingView })),
)
const DemandReportingView = lazy(() =>
  import("./containers/DemandReportingView").then((m) => ({
    default: m.DemandReportingView,
  })),
)
const MasterDataView = lazy(() =>
  import("./containers/MasterDataView").then((m) => ({ default: m.MasterDataView })),
)
const DigitalTwinView = lazy(() =>
  import("./containers/DigitalTwinView").then((m) => ({ default: m.DigitalTwinView })),
)
const LinearView = lazy(() =>
  import("./containers/LinearView").then((m) => ({ default: m.LinearView })),
)
const NucleusWorkflowsView = lazy(() =>
  import("./containers/NucleusWorkflowsView").then((m) => ({ default: m.NucleusWorkflowsView })),
)
const DocsView = lazy(() =>
  import("./containers/DocsView").then((m) => ({ default: m.DocsView })),
)
const ReplenishmentView = lazy(() =>
  import("./containers/ReplenishmentView").then((m) => ({
    default: m.ReplenishmentView,
  })),
)
// Admin (user management) — atomic-users only; the view itself redirects others.
const AdminView = lazy(() =>
  import("./containers/AdminView").then((m) => ({ default: m.AdminView })),
)

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    children: [
      // Cognito OAuth redirect routes — Amplify processes auth tokens on these pages
      { path: "callback", element: <Navigate to="/" replace /> },
      { path: "logout", element: <Navigate to="/" replace /> },
      {
        element: <DashboardLayout />,
        children: [
          { index: true, element: <Navigate to="/data" replace /> },
          {
            path: "data-hub",
            element: (
              <LazyRoute>
                <DataHubView />
              </LazyRoute>
            ),
          },
          {
            path: "data-hub/source/:name",
            element: (
              <LazyRoute>
                <DataSourceDetailView />
              </LazyRoute>
            ),
          },
          {
            path: "data-hub/:folder",
            element: (
              <LazyRoute>
                <MappingDetailView />
              </LazyRoute>
            ),
          },
          { path: "master-data", element: <Navigate to="/data" replace /> },
          {
            path: "data",
            element: (
              <LazyRoute>
                <MasterDataView />
              </LazyRoute>
            ),
          },
          {
            // `chrome: "slim"` swaps the tall RunHeader for the icon-only
            // SlimHeader so the full-bleed twin canvas gets the extra vertical
            // space, while account + feedback stay reachable. Any page that
            // wants more room can opt in the same way. See DashboardLayout.
            path: "digital-twin",
            handle: { chrome: "slim" },
            element: (
              <LazyRoute>
                <DigitalTwinView />
              </LazyRoute>
            ),
          },
          {
            path: "linear",
            element: (
              <LazyRoute>
                <LinearView />
              </LazyRoute>
            ),
          },
          {
            path: "demand/validation",
            element: (
              <LazyRoute>
                <RetailValidationView />
              </LazyRoute>
            ),
          },
          {
            // Demand primary tab: the native pivot over the engine's demand
            // artifact, in the app's common module format (static route wins
            // over demand/:reportSlug below by route ranking, like
            // demand/validation above).
            path: "demand",
            element: (
              <LazyRoute>
                <DemandReportingView />
              </LazyRoute>
            ),
          },
          {
            // Slugged demand reports keep serving the reporting embed pinned
            // to Sales Forecast so in-frame report nav stays under /demand,
            // mirroring reporting/:reportSlug?.
            path: "demand/:reportSlug",
            element: (
              <LazyRoute>
                <ReportingView
                  reportSlug={DEMAND_REPORT_SLUG}
                  basePath={DEMAND_BASE_PATH}
                />
              </LazyRoute>
            ),
          },
          {
            path: "replenishment",
            element: (
              <LazyRoute>
                <ReplenishmentView />
              </LazyRoute>
            ),
          },
          { path: "shipments", element: <RetailerSupplyView /> },
          { path: "shipments/planning-console", element: <RetailerSupplyView /> },
          {
            path: "shipments/reporting",
            element: (
              <LazyRoute>
                <ShipmentsReportingView />
              </LazyRoute>
            ),
          },
          {
            path: "shipments/validation",
            element: (
              <LazyRoute>
                <ShipmentsValidationView />
              </LazyRoute>
            ),
          },
          {
            path: "shipments/configuration",
            element: (
              <LazyRoute>
                <ShipmentsConfigurationView />
              </LazyRoute>
            ),
          },
          {
            // Optional :reportSlug — bare tab and deep links share one route
            // object so in-frame report navigation never remounts the iframe.
            // Optional :qfSlug carries a quick filter as a path segment
            // (/reporting/TimeSeries/SalesForecast), mirroring queryviz's own
            // canonical report paths.
            path: "reporting/:reportSlug?/:qfSlug?",
            element: (
              <LazyRoute>
                <ReportingView />
              </LazyRoute>
            ),
          },
          {
            path: "runs/history",
            element: (
              <LazyRoute>
                <RunHistoryView />
              </LazyRoute>
            ),
          },
          {
            path: "docs",
            element: (
              <LazyRoute>
                <DocsView />
              </LazyRoute>
            ),
          },
          ...(nucleusWorkflowsEnabled
            ? [
                {
                  path: "workflows",
                  element: (
                    <LazyRoute>
                      <NucleusWorkflowsView />
                    </LazyRoute>
                  ),
                },
              ]
            : []),
          { path: "nucleus", element: <NucleusChatView /> },
          {
            path: "user-admin",
            element: (
              <LazyRoute>
                <AdminView />
              </LazyRoute>
            ),
          },
        ],
      },
    ],
  },
])

export default router
